import React, { useState, useEffect } from 'react';
import {
  ProjectPlanningCategory,
  ProjectTypeDefinition,
  ProjectTypeRecommendation
} from '../../types/project-types';
import { Project } from '../../types';
import { useProjectTypes, useProjectTypeWizard } from '../../hooks/useProjectTypes';
import { TypeSelectionStep } from './TypeSelectionStep';
import { ConfigurationStep } from './ConfigurationStep';
import { ReviewStep } from './ReviewStep';

interface ProjectTypeWizardProps {
  project?: Partial<Project>;
  onComplete: (config: {
    selectedType: ProjectPlanningCategory;
    configuration: Record<string, any>;
    metadata: Record<string, any>;
  }) => void;
  onCancel: () => void;
  isOpen: boolean;
}

export const ProjectTypeWizard: React.FC<ProjectTypeWizardProps> = ({
  project,
  onComplete,
  onCancel,
  isOpen
}) => {
  const { getProjectTypeDefinitions, getDefinition, getRecommendation, validateProject } = useProjectTypes();
  const { wizardState, updateWizardState, goToNextStep, goToPreviousStep, completeStep, resetWizard } = useProjectTypeWizard();

  const [recommendations, setRecommendations] = useState<ProjectTypeRecommendation[]>([]);
  const [selectedDefinition, setSelectedDefinition] = useState<ProjectTypeDefinition | null>(null);

  // Get recommendations when project description changes
  useEffect(() => {
    if (project?.description) {
      const recs = getRecommendation(project.description);
      setRecommendations(recs);

      if (recs.length > 0) {
        updateWizardState({
          suggestedCategory: recs[0].category,
          confidence: recs[0].confidence / 100
        });
      }
    }
  }, [project?.description, getRecommendation, updateWizardState]);

  // Update selected definition when category changes
  useEffect(() => {
    if (wizardState.selectedCategory) {
      const definition = getDefinition(wizardState.selectedCategory);
      setSelectedDefinition(definition);
      updateWizardState({
        totalSteps: 3 + definition.planningSteps.length
      });
    }
  }, [wizardState.selectedCategory, getDefinition, updateWizardState]);

  // Reset wizard when opened
  useEffect(() => {
    if (isOpen) {
      resetWizard();
    }
  }, [isOpen, resetWizard]);

  const handleTypeSelection = (category: ProjectPlanningCategory) => {
    updateWizardState({
      selectedCategory: category,
      formData: { ...wizardState.formData, selectedType: category }
    });
    completeStep('type_selection');
    goToNextStep();
  };

  const handleConfigurationUpdate = (data: Record<string, any>) => {
    updateWizardState({
      formData: { ...wizardState.formData, ...data }
    });
  };

  const handleConfigurationComplete = () => {
    if (selectedDefinition) {
      // Validate configuration
      const validation = validateProject(
        { ...project, planningMetadata: { steps: [] } },
        selectedDefinition
      );

      if (validation.isValid) {
        completeStep('configuration');
        goToNextStep();
      } else {
        // Set validation errors
        validation.errors.forEach(error => {
          updateWizardState({
            validationErrors: {
              ...wizardState.validationErrors,
              general: error
            }
          });
        });
      }
    }
  };

  const handleComplete = () => {
    if (wizardState.selectedCategory && selectedDefinition) {
      const configuration = {
        selectedType: wizardState.selectedCategory,
        configuration: wizardState.formData,
        metadata: {
          triggerType: selectedDefinition.characteristics.triggerType,
          timeHorizon: selectedDefinition.characteristics.timeHorizon,
          measurementFocus: selectedDefinition.characteristics.measurementFocus,
          planningSteps: selectedDefinition.planningSteps,
          artifacts: selectedDefinition.artifactTypes
        }
      };

      onComplete(configuration);
    }
  };

  const renderStepContent = () => {
    switch (wizardState.currentStep) {
      case 0:
        return (
          <TypeSelectionStep
            recommendations={recommendations}
            availableTypes={getProjectTypeDefinitions()}
            selectedType={wizardState.selectedCategory}
            onSelect={handleTypeSelection}
            project={project}
          />
        );

      case 1:
        return selectedDefinition ? (
          <ConfigurationStep
            definition={selectedDefinition}
            formData={wizardState.formData}
            validationErrors={wizardState.validationErrors}
            onUpdate={handleConfigurationUpdate}
            onComplete={handleConfigurationComplete}
            project={project}
          />
        ) : null;

      case 2:
        return selectedDefinition ? (
          <ReviewStep
            definition={selectedDefinition}
            formData={wizardState.formData}
            project={project}
            onComplete={handleComplete}
          />
        ) : null;

      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch (wizardState.currentStep) {
      case 0:
        return 'Projekttyp auswählen';
      case 1:
        return 'Konfiguration';
      case 2:
        return 'Überprüfung';
      default:
        return 'Projekttyp-Assistent';
    }
  };

  const canGoNext = () => {
    switch (wizardState.currentStep) {
      case 0:
        return wizardState.selectedCategory !== undefined;
      case 1:
        return Object.keys(wizardState.validationErrors).length === 0;
      default:
        return true;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 px-6 py-4 border-b">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {getStepTitle()}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Schritt {wizardState.currentStep + 1} von {wizardState.totalSteps}
              </p>
            </div>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mt-4 bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{
                width: `${((wizardState.currentStep + 1) / wizardState.totalSteps) * 100}%`
              }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-200px)]">
          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t flex justify-between">
          <div>
            {wizardState.currentStep > 0 && (
              <button
                onClick={goToPreviousStep}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                ← Zurück
              </button>
            )}
          </div>

          <div className="flex space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
            >
              Abbrechen
            </button>

            {wizardState.currentStep < wizardState.totalSteps - 1 ? (
              <button
                onClick={goToNextStep}
                disabled={!canGoNext()}
                className={`px-4 py-2 rounded-lg font-medium ${
                  canGoNext()
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Weiter →
              </button>
            ) : (
              <button
                onClick={handleComplete}
                disabled={!canGoNext()}
                className={`px-4 py-2 rounded-lg font-medium ${
                  canGoNext()
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Abschließen
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
